/**
 * Pure path helpers for Apps, mirroring homePaths and inboxPaths.
 *
 * Apps is its own area: `/$org/apps` lists every app, `/$org/apps/<id>`
 * opens one (Contract Intelligence included), `/$org/apps/custom/<id>` a
 * custom MCP server. The rail selects Apps and the Apps sidebar stays beside
 * all of them.
 *
 * @module components/apps/appsPaths
 */

/** First path segments that are routes, never an organization slug. */
const RESERVED_TOP_LEVEL_SEGMENTS: ReadonlySet<string> = new Set([
  "automations",
  "compliance",
  "connect",
  "connections",
  "inbox",
  "library",
  "pair",
  "regulation",
  "settings",
  "usage",
]);

export const APPS_SEGMENT = "apps";

/** True anywhere under `/$org/apps`; drives nav selection, never content. */
export function isAppsPathname(pathname: string): boolean {
  const [org, section] = pathname.split("/").filter(Boolean);
  return org !== undefined && !RESERVED_TOP_LEVEL_SEGMENTS.has(org) && section === APPS_SEGMENT;
}

/** The app a pathname opens (`imanage`, `contract-intelligence`, `custom/<id>`), or null on the list. */
export function appFromPathname(pathname: string): string | null {
  if (!isAppsPathname(pathname)) return null;
  const rest = pathname.split("/").filter(Boolean).slice(2);
  return rest.length === 0 ? null : rest.join("/");
}

/**
 * Old Governance → Integrations addresses, mapped to their Apps address, so
 * bookmarks, inbox notifications and server links keep working. Null for
 * anything that is not an old integrations address.
 */
export type LegacyIntegrationsTarget =
  | { readonly to: "/$org/apps" }
  | { readonly to: "/$org/apps/$appId"; readonly appId: string }
  | { readonly to: "/$org/apps/custom/$serverId"; readonly serverId: string };

export function legacyIntegrationsRedirect(pathname: string): LegacyIntegrationsTarget | null {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[1] !== "compliance" || segments[2] !== "integrations") return null;
  const rest = segments.slice(3);
  if (rest.length === 0) return { to: "/$org/apps" };
  if (rest[0] === "custom" && rest[1])
    return { to: "/$org/apps/custom/$serverId", serverId: decodeURIComponent(rest[1]) };
  return { to: "/$org/apps/$appId", appId: decodeURIComponent(rest[0]!) };
}

/**
 * Apps that are their own area, with a sidebar of their own beside them.
 * Every other app opens as one full page, and the Apps page has no sidebar.
 */
export const APP_AREA_IDS = ["imanage", "contract-intelligence", "probe"] as const;
export type AppAreaId = (typeof APP_AREA_IDS)[number];

export const isAppAreaId = (value: string | undefined): value is AppAreaId =>
  APP_AREA_IDS.some((id) => id === value);

/** The app area a pathname is inside (`/$org/apps/imanage/…` → "imanage"), or null. */
export function appAreaFromPathname(pathname: string): AppAreaId | null {
  if (!isAppsPathname(pathname)) return null;
  const appId = pathname.split("/").filter(Boolean)[2];
  return isAppAreaId(appId) ? appId : null;
}
